import { useDebouncedFunction } from '@strata/helper-functions';
import { ChatWithConversationPane } from '@strata/react-chat';
import { Spinner } from '@strata/react-loading-animation';
import { SuccessToast } from '@strata/react-toast-message';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import {
    archiveNote,
    createNote,
    findNotes,
    getNoteById,
    postMessage,
    softDeleteMessage,
} from 'api/userSupportAPI';
import { NewNoteDialog } from 'components/shared/dialogs/NewNoteDialog';
import { useUserProfile } from 'services/APIHooks';
import { handleError } from 'services/Error';

const mapMessage = (message) => ({
    content: message?.content,
    conversationId: message?.conversation_id,
    senderFirstName: message?.first_name,
    isSupportMsg: message?.is_support_msg,
    senderLastName: message?.last_name,
    messageId: message?.message_id,
    messageType: message?.message_type,
    sentTime: message?.sent_time,
    upn: message?.upn,
});

const mapNote = (conversation) => ({
    conversationId: conversation.conversation_id,
    createdByUpn: conversation.created_by_upn,
    createdByFirstName: conversation.created_by_first_name,
    createdByLastName: conversation.created_by_last_name,
    createdTime: conversation.created_time,
    details: conversation.details,
    isNote: conversation?.is_note,
    referralId: conversation.referral_id,
    supplierNpi: conversation?.supplier_npi,
    topicId: conversation?.topic_id,
    messages: conversation?.messages?.map(mapMessage),
});

export const Notes = ({ referralId = undefined }) => {
    const [notes, setNotes] = useState();
    const [showNewNoteDialog, setShowNewNoteDialog] = useState(false);
    const [selectedConversationId, setSelectedConversationId] = useState();
    const [selectedConversation, setSelectedConversation] = useState();
    const [chatLoading, setChatLoading] = useState(false);
    const { userProfile } = useUserProfile();
    const [searchTerm, setSearchTerm] = useState();
    const debouncedSearch = useDebouncedFunction();

    const getNoteMessages = useCallback(
        async (conversationId) => {
            if (!selectedConversation) {
                setChatLoading(true);
            }
            try {
                const noteAndMessages = await getNoteById({
                    conversationId,
                });
                setSelectedConversation(mapNote(noteAndMessages));
            } catch (error) {
                handleError('Unable to retrieve note.', error);
            }
            setChatLoading(false);
        },
        [selectedConversation],
    );

    const setMappedNotes = (notesList) => {
        const mappedNotes = notesList.map(mapNote);
        setNotes(mappedNotes);
    };

    const fetchNotes = useCallback(async () => {
        try {
            const notesList = await findNotes({ referralId });
            setMappedNotes(notesList);
        } catch (error) {
            handleError('Unable to retrieve notes for referral.', error);
        }
    }, [referralId]);

    useEffect(() => {
        const defaultNote = notes?.[0]?.conversationId;
        if (!selectedConversation) {
            if (selectedConversationId) {
                // eslint-disable-next-line react-hooks/set-state-in-effect
                getNoteMessages(selectedConversationId);
            } else if (defaultNote) {
                setSelectedConversationId(defaultNote);
                getNoteMessages(defaultNote);
            }
        }
    }, [getNoteMessages, notes, selectedConversation, selectedConversationId]);

    useEffect(() => {
        // If user profile changes, refetch notes
        if (referralId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            fetchNotes();
            setSelectedConversation();
        }
    }, [fetchNotes, referralId, userProfile]);

    if (!referralId || !notes) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                }}
            >
                <Spinner size={Spinner.Size.LARGE} intent={Spinner.Intent.NONE} />
            </div>
        );
    }

    return (
        <>
            <NewNoteDialog
                isOpen={showNewNoteDialog}
                onCancel={() => setShowNewNoteDialog(false)}
                onCreate={async ({ subject }) => {
                    if (!selectedConversation) {
                        setChatLoading(true);
                    }
                    try {
                        const newNote = await createNote({
                            referralId,
                            details: {
                                details: {
                                    subject,
                                },
                                created_by_upn: userProfile.upn,
                            },
                        });
                        const newMappedNote = mapNote(newNote);
                        setSelectedConversation(newMappedNote);
                        setSelectedConversationId(newMappedNote.conversationId);
                        SuccessToast({ message: `New note created: ${subject}.` });
                    } catch (error) {
                        handleError('Unable to create a new note.', error);
                    }
                    fetchNotes();
                    setShowNewNoteDialog(false);
                    setChatLoading(false);
                }}
            />
            <ChatWithConversationPane
                conversations={notes}
                conversationType={ChatWithConversationPane.ConversationType.NOTES}
                onSearch={(value) => {
                    if (value?.length > 0) {
                        debouncedSearch(async () => {
                            const foundMessages = await findNotes({
                                searchTerm: value,
                                referralId,
                            });
                            setMappedNotes(foundMessages);
                            setSearchTerm(value);
                        });
                    } else {
                        setSearchTerm();
                        fetchNotes();
                    }
                }}
                onConversationSelect={async (conversationId) => {
                    setSelectedConversationId(conversationId);
                    await getNoteMessages(conversationId);
                }}
                selectedConversation={selectedConversation}
                onNewConversationClick={() => setShowNewNoteDialog(true)}
                onConversationDelete={async (conversationId) => {
                    try {
                        await archiveNote({ conversationId });
                        SuccessToast({ message: 'Note deleted successfully.' });
                        if (conversationId === selectedConversationId) {
                            setSelectedConversationId();
                            setSelectedConversation();
                        }
                        fetchNotes();
                    } catch (error) {
                        handleError('Unable to delete note.', error);
                    }
                }}
                onMessageDelete={async ({ messageId, conversationId }) => {
                    try {
                        await softDeleteMessage({ messageId });
                        SuccessToast({ message: 'Message deleted successfully.' });
                        await getNoteMessages(conversationId);
                    } catch (error) {
                        handleError('Unable to delete message.', error);
                    }
                }}
                onMessageSend={async ({ conversationId, details }) => {
                    try {
                        await postMessage({
                            conversationId,
                            details: {
                                upn: userProfile.upn,
                                content: {
                                    message: details.message,
                                },
                                is_support_msg: true,
                            },
                        });
                        SuccessToast({ message: 'Message saved successfully.' });
                        await getNoteMessages(conversationId);
                    } catch (error) {
                        handleError('Unable to save message.', error);
                    }
                }}
                highlightText={searchTerm}
                chatLoading={chatLoading}
            />
        </>
    );
};

Notes.propTypes = {
    referralId: PropTypes.string,
};
